import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import Modal from '../../components/Modal';
import PaymentQRCode from '../../components/PaymentQRCode';
import CollapsibleWidget from '../../components/CollapsibleWidget';
import EventComments from '../../components/messaging/EventComments';
import EventAnnouncements from '../../components/messaging/EventAnnouncements';
import MemberEventRequirements from '../../components/MemberEventRequirements';
import { useNotifications } from '../../context/NotificationContext';
import '../admin/EventCalendarPage.css';
import '../eventCardClassic.css';
import './MyCalendarPage.css';
import './EventInvitationsPage.css';
import PageHeader from '../../components/PageHeader';
import { translateError } from '../../utils/errorMessages';

// Extended event type with attendance info (same as MyCalendarPage)
interface MyCalendarEvent {
  _id: string;
  eventId: string;
  name: string;
  description: string;
  location?: string;
  startTime: string;
  endTime: string;
  template: string | null;
  attending: boolean | null;
  defaultAttendance: boolean | null;
  note?: string;
  price?: number;
  /** Available events only: what signing up would cost (never owed). */
  listPrice?: number;
  isPaid?: boolean;
  /** Invited via activity/season, not on the roster (D17). */
  available?: boolean;
  member?: { _id: string; name: string; color: string };
  predictedCoverage?: {
    method: 'calendar' | 'time' | 'entry' | 'needs_payment' | 'free';
    memberMembershipId: number | null;
    label?: { key: string; params?: Record<string, string | number> };
  };
}

// Group events by eventId for tile display (user may have multiple members per event)
interface GroupedEvent {
  eventId: string;
  name: string;
  description: string;
  location?: string;
  startTime: string;
  endTime: string;
  price: number;
  /** Every member entry is only invited, none signed up. */
  available: boolean;
  memberEvents: MyCalendarEvent[];
}

const toGroup = (memberEvents: MyCalendarEvent[]): GroupedEvent => {
  const first = memberEvents[0];
  const available = memberEvents.every((e) => e.available);
  return {
    eventId: first.eventId,
    name: first.name,
    description: first.description,
    location: first.location,
    startTime: first.startTime,
    endTime: first.endTime,
    price: available ? (first.listPrice ?? 0) : Math.max(...memberEvents.map((e) => e.price ?? 0)),
    available,
    memberEvents,
  };
};

const EventInvitationsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { markInvitationsRead } = useNotifications();
  const [events, setEvents] = useState<MyCalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<GroupedEvent | null>(null);
  const [updatingAttendance, setUpdatingAttendance] = useState(false);
  const [noteValues, setNoteValues] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState<'details' | 'requirements' | 'comments'>('details');
  const [activeMemberId, setActiveMemberId] = useState<string | null>(null);

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.my.events({ includeAvailable: 1 });
      const data = response.data as { events: MyCalendarEvent[] };
      setEvents(data.events);
      setLoading(false);
    } catch (err: any) {
      setError(translateError(err, t));
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Mark invitation notifications as read when page opens
  useEffect(() => {
    markInvitationsRead();
  }, [markInvitationsRead]);

  // Filter for manual events only (template is null/falsy) and upcoming only
  const groupedEvents = useMemo(() => {
    const now = new Date();

    // Filter: manual events (no template) and upcoming (startTime >= now)
    const filtered = events.filter(
      (e) => !e.template && new Date(e.startTime) >= now
    );

    // Group by eventId
    const groupMap = new Map<string, MyCalendarEvent[]>();
    for (const event of filtered) {
      const existing = groupMap.get(event.eventId);
      if (existing) existing.push(event);
      else groupMap.set(event.eventId, [event]);
    }

    // Sort by startTime ascending
    return Array.from(groupMap.values(), toGroup).sort(
      (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
    );
  }, [events]);

  // Deep-link: auto-open event modal when ?eventId= is in the URL
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const eventId = searchParams.get('eventId');
    if (!eventId || events.length === 0) return;

    // Try filtered groupedEvents first, then fall back to all events (past/template)
    let group = groupedEvents.find(g => g.eventId === eventId);
    if (!group) {
      const memberEvents = events.filter(e => e.eventId === eventId);
      if (memberEvents.length > 0) group = toGroup(memberEvents);
    }
    if (group) {
      setSelectedGroup(group);
      setActiveTab('details');
      const firstMemberId = group.memberEvents[0]?.member?._id || null;
      setActiveMemberId(firstMemberId);
      setModalOpen(true);
    }
    setSearchParams({}, { replace: true });
  }, [searchParams, events, groupedEvents, setSearchParams]);

  // Update attendance for a member on an event
  const handleUpdateAttendance = async (event: MyCalendarEvent, newAttending: boolean | null) => {
    if (!event.member) return;

    setUpdatingAttendance(true);
    try {
      // Include current note value if user has edited it
      const currentNote = noteValues[event._id];
      await api.my.updateAttendance(event.eventId, event.member._id, newAttending, currentNote);

      // Update local state
      setEvents((prevEvents) =>
        prevEvents.map((e) =>
          e._id === event._id ? { ...e, attending: newAttending, ...(currentNote !== undefined ? { note: currentNote } : {}) } : e
        )
      );
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setUpdatingAttendance(false);
    }
  };

  // Save note explicitly
  const handleSaveNote = async (event: MyCalendarEvent) => {
    if (!event.member) return;
    const noteValue = noteValues[event._id] ?? event.note ?? '';
    if (noteValue === (event.note || '')) return; // No change

    setUpdatingAttendance(true);
    try {
      await api.my.updateAttendance(event.eventId, event.member._id, event.attending, noteValue);
      setEvents((prevEvents) =>
        prevEvents.map((e) =>
          e._id === event._id ? { ...e, note: noteValue } : e
        )
      );
      // Clear from local edits since it's now saved
      setNoteValues((prev) => {
        const next = { ...prev };
        delete next[event._id];
        return next;
      });
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setUpdatingAttendance(false);
    }
  };

  const handleOpenDetail = (group: GroupedEvent) => {
    setSelectedGroup(group);
    setActiveTab('details');
    const firstMemberId = group.memberEvents[0]?.member?._id || null;
    setActiveMemberId(firstMemberId);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedGroup(null);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const locale = i18n.language === 'cs' ? 'cs-CZ' : 'en-US';
    return date.toLocaleDateString(locale, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const locale = i18n.language === 'cs' ? 'cs-CZ' : 'en-US';
    return date.toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isMultiDay = (startStr: string, endStr: string) => {
    const s = new Date(startStr);
    const e = new Date(endStr);
    return s.toDateString() !== e.toDateString();
  };

  const formatEventDateTime = (dateTime: string): string => {
    const date = new Date(dateTime);
    const locale = i18n.language === 'cs' ? 'cs-CZ' : 'en-US';
    return date.toLocaleString(locale, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get the current member events for the selected group (reflects local state updates)
  const selectedMemberEvents = useMemo(() => {
    if (!selectedGroup) return [];
    return events.filter(
      (e) => e.eventId === selectedGroup.eventId && !e.template
    );
  }, [selectedGroup, events]);

  if (loading) {
    return (
      <div className="classic-event-list">
        <div className="public-events-container">
          <p className="loading-text">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="classic-event-list">
        <div className="public-events-container">
          <p className="error-message">{t('common.error')}: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="invitation-page classic-event-list">
      <div className="public-events-header">
        <PageHeader title={t('eventInvitations.title')} helpKey="help.user.invitations" />
        <p className="subtitle">{t('eventInvitations.subtitle')}</p>
      </div>

      {groupedEvents.length === 0 ? (
        <div className="no-events">
          <p>{t('eventInvitations.noInvitations')}</p>
        </div>
      ) : (
        <div className="events-grid">
          {groupedEvents.map((group) => (
            <div key={group.eventId} className={`event-card${group.available ? ' event-card--available' : ''}`}>
              <div className="event-card-content">
                <div className="event-card-header">
                  <h2 className="event-name">{group.name}</h2>
                  {group.available && (
                    <span className="invitation-available-badge">{t('calendar.available')}</span>
                  )}
                </div>

                <div className="event-details">
                  <div className="detail-row">
                    <span className="detail-icon">📅</span>
                    <span>
                      {isMultiDay(group.startTime, group.endTime)
                        ? `${formatDate(group.startTime)} – ${formatDate(group.endTime)}`
                        : formatDate(group.startTime)}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-icon">⏰</span>
                    <span>{formatTime(group.startTime)} - {formatTime(group.endTime)}</span>
                  </div>

                  {group.location && (
                    <div className="detail-row">
                      <span className="detail-icon">📍</span>
                      <span>{group.location}</span>
                    </div>
                  )}

                  {group.price > 0 && (
                    <div className="detail-row">
                      <span className="detail-icon">💰</span>
                      <span className="price">{group.price} CZK</span>
                    </div>
                  )}
                </div>

                {group.description && (
                  <p className="event-description" style={{ whiteSpace: 'pre-line' }}>{group.description}</p>
                )}

                {/* Member badges when user has multiple members on same event */}
                {group.memberEvents.length > 0 && (
                  <div className="invitation-member-badges">
                    {group.memberEvents.map((me) =>
                      me.member ? (
                        <span
                          key={me._id}
                          className="invitation-member-badge"
                          style={{ backgroundColor: me.member.color || 'var(--orange-primary)' }}
                        >
                          {me.member.name}
                        </span>
                      ) : null
                    )}
                  </div>
                )}

                <button
                  className="register-link"
                  onClick={() => handleOpenDetail(group)}
                  style={{ border: 'none', cursor: 'pointer' }}
                >
                  {t('eventInvitations.detail')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Event Detail Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        title={selectedGroup?.name || ''}
        size="xlarge"
      >
        {selectedGroup && (
          <div className="modal-event-edit-layout">
            {/* Mobile Tab Switcher */}
            <div className="tab-switcher mobile-only">
              <button
                className={activeTab === 'details' ? 'active' : ''}
                onClick={() => setActiveTab('details')}
              >
                {t('eventDetails.tabDetails')}
              </button>
              <button
                className={activeTab === 'requirements' ? 'active' : ''}
                onClick={() => setActiveTab('requirements')}
              >
                {t('requirements.widgetTitle')}
              </button>
              <button
                className={activeTab === 'comments' ? 'active' : ''}
                onClick={() => setActiveTab('comments')}
              >
                {t('eventDetails.tabComments')}
              </button>
            </div>

            {/* Left column - Event details */}
            <div className={`modal-edit-form ${activeTab === 'details' ? 'active' : ''}`}>
              <div className="modal-event-details">
                <div className="event-detail-row">
                  <strong>{t('eventDetails.description')}</strong>
                  <p>{selectedGroup.description || '-'}</p>
                </div>
                <div className="event-detail-row">
                  <strong>{t('eventDetails.startTime')}</strong>
                  <p>{formatEventDateTime(selectedGroup.startTime)}</p>
                </div>
                <div className="event-detail-row">
                  <strong>{t('eventDetails.endTime')}</strong>
                  <p>{formatEventDateTime(selectedGroup.endTime)}</p>
                </div>

                {/* Per-member attendance sections */}
                <div className="invitation-members-section">
                  {selectedMemberEvents.length > 1 && (
                    <div className="member-tab-switcher">
                      {selectedMemberEvents.map((me) =>
                        me.member ? (
                          <button
                            key={me._id}
                            className={activeMemberId === me.member._id ? 'active' : ''}
                            style={activeMemberId === me.member._id
                              ? { backgroundColor: me.member.color || 'var(--orange-primary)', borderColor: me.member.color || 'var(--orange-primary)' }
                              : { color: me.member.color || 'var(--text-secondary)' }}
                            onClick={() => setActiveMemberId(me.member!._id)}
                          >
                            {me.member.name}
                          </button>
                        ) : null
                      )}
                    </div>
                  )}

                  {selectedMemberEvents
                    .filter((me) => selectedMemberEvents.length === 1 || me.member?._id === activeMemberId)
                    .map((memberEvent) => (
                    <div key={memberEvent._id} className="invitation-member-detail">
                      {memberEvent.member && selectedMemberEvents.length === 1 && (
                        <div className="event-detail-row">
                          <strong>{t('eventDetails.member')}</strong>
                          <p style={{ color: memberEvent.member.color, fontWeight: 600 }}>
                            {memberEvent.member.name}
                          </p>
                        </div>
                      )}

                      {memberEvent.predictedCoverage && memberEvent.predictedCoverage.method !== 'free' && memberEvent.predictedCoverage.label && (
                        <div className="event-detail-row">
                          <strong>{t('events.coveredBy')}</strong>
                          <p style={{ color: 'var(--text-muted)' }}>
                            {t(memberEvent.predictedCoverage.label.key, memberEvent.predictedCoverage.label.params || {})}
                          </p>
                        </div>
                      )}

                      {memberEvent.available && (
                        <div className="available-event-notice" role="status">
                          <span className="attendance-badge available">{t('calendar.available')}</span>
                          <p>{t('calendar.availableHint')}</p>
                          {(memberEvent.listPrice ?? 0) > 0 && (
                            <p>
                              <strong>{t('events.price')}:</strong> {memberEvent.listPrice} CZK
                            </p>
                          )}
                        </div>
                      )}

                      {!memberEvent.available && (memberEvent.price ?? 0) > 0 && memberEvent.member && (
                        <div className="attendance-section" style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1.5rem', marginTop: '1.5rem' }}>
                          <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                            <div style={{ flex: 1, minWidth: '200px' }}>
                              <strong>{t('events.price')}</strong>
                              <p style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--orange-primary)' }}>
                                {memberEvent.price} CZK
                              </p>
                              {memberEvent.isPaid ? (
                                <div style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  padding: '0.5rem 1rem',
                                  background: 'var(--success-color, #38a169)',
                                  color: 'white',
                                  borderRadius: '4px',
                                  fontWeight: 'bold',
                                  marginTop: '0.5rem'
                                }}>
                                  <span style={{ fontSize: '1.2rem' }}>✓</span>
                                  {t('payments.paid')}
                                </div>
                              ) : (
                                <div style={{ marginTop: '1rem' }}>
                                  <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                                    {t('publicEvent.paymentQRDesc')}
                                  </p>
                                  <div style={{ background: 'white', padding: '8px', borderRadius: '4px', display: 'inline-block' }}>
                                    <PaymentQRCode
                                      eventId={memberEvent.eventId}
                                      memberId={memberEvent.member._id}
                                      size={150}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {!memberEvent.available && (
                      <div className="attendance-section">
                        <strong>{t('attendance.yourAttendance')}:</strong>
                        <div className="attendance-dropdown-container">
                          <select
                            className={`attendance-dropdown ${memberEvent.attending === true ? 'yes' : memberEvent.attending === false ? 'no' : 'maybe'}`}
                            value={memberEvent.attending === true ? 'yes' : memberEvent.attending === false ? 'no' : 'null'}
                            onChange={(e) => {
                              const val = e.target.value;
                              const newAttending = val === 'yes' ? true : val === 'no' ? false : null;
                              handleUpdateAttendance(memberEvent, newAttending);
                            }}
                            disabled={updatingAttendance}
                          >
                            <option value="yes">{t('common.yes')} ✓</option>
                            <option value="no">{t('common.no')} ✗</option>
                            <option value="null">{t('common.maybe')} ?</option>
                          </select>
                          {updatingAttendance && <span className="attendance-updating">{t('common.saving')}</span>}
                        </div>
                        <div className="attendance-note-wrapper">
                          <textarea
                            className="attendance-note-input"
                            placeholder={t('attendance.notePlaceholder')}
                            value={noteValues[memberEvent._id] ?? memberEvent.note ?? ''}
                            maxLength={500}
                            rows={2}
                            onChange={(e) => setNoteValues((prev) => ({ ...prev, [memberEvent._id]: e.target.value }))}
                          />
                          {(noteValues[memberEvent._id] !== undefined && noteValues[memberEvent._id] !== (memberEvent.note || '')) && (
                            <button
                              type="button"
                              className="attendance-note-save-btn"
                              onClick={() => handleSaveNote(memberEvent)}
                              disabled={updatingAttendance}
                            >
                              {updatingAttendance ? t('common.saving') : t('common.save')}
                            </button>
                          )}
                        </div>
                      </div>
                      )}

                      {/* Per-member requirements widget (not for available-only entries) */}
                      {memberEvent.member && !memberEvent.available && (
                        <div style={{ marginTop: '1rem' }}>
                          <MemberEventRequirements
                            eventId={memberEvent.eventId}
                            memberId={memberEvent.member._id}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right column - Widgets */}
            <div className={`modal-edit-widgets ${activeTab === 'requirements' || activeTab === 'comments' ? 'active' : ''}`}>
              <EventAnnouncements eventId={selectedGroup.eventId} />
              <CollapsibleWidget
                title={t('eventDetails.tabComments')}
                defaultExpanded={true}
              >
                <EventComments eventId={selectedGroup.eventId} />
              </CollapsibleWidget>

              {selectedMemberEvents.length === 1 && selectedMemberEvents[0].member && !selectedMemberEvents[0].available && (
                <CollapsibleWidget
                  title={t('requirements.widgetTitle')}
                  defaultExpanded={true}
                >
                  <MemberEventRequirements
                    eventId={selectedGroup.eventId}
                    memberId={selectedMemberEvents[0].member._id}
                  />
                </CollapsibleWidget>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default EventInvitationsPage;
